// Keeps every visitor on the latest deployed build without a manual hard
// refresh (Ctrl+F5). The HTML shell (index.html) is served no-cache, so a
// fresh page load always pulls the newest bundle - this module covers the
// remaining case: a tab that is already open when a new build ships.
//
// Two cooperating mechanisms, both chosen to never destroy in-progress work
// (e.g. an admin editing a form):
//   1. Version polling - fetch asset-manifest.json (bypassing the browser
//      cache) on an interval and whenever the tab regains focus. A changed
//      main.js hash means a new build was deployed.
//   2. Deferred reload - when an update is pending we reload only at a SAFE
//      moment: the next in-app navigation (react-router uses the History API)
//      or a back/forward. We deliberately do NOT reload out from under a user
//      who is sitting on a page, so unsaved input is never lost.
//
// Plus a ChunkLoadError guard: after a deploy the previous build's lazy chunks
// no longer exist on the server, so if one fails to load we hard-reload
// immediately (guarded against reload loops).

const MANIFEST_URL = '/asset-manifest.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const RELOAD_GUARD_KEY = 'pe:auto-reloaded-at';
const RELOAD_GUARD_WINDOW_MS = 30 * 1000; // never auto-reload twice within 30s

let currentBundle: string | null = null;
let updatePending = false;

const fetchBundleHash = async (): Promise<string | null> => {
  try {
    const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data: { files?: Record<string, string> } = await res.json();
    return data?.files?.['main.js'] ?? null;
  } catch {
    return null;
  }
};

const reloadOnce = (): void => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_GUARD_WINDOW_MS) return; // loop guard
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage may be unavailable (privacy mode); a single reload is
    // still safe, only the loop guard is lost.
  }
  window.location.reload();
};

const checkForUpdate = async (): Promise<void> => {
  const latest = await fetchBundleHash();
  if (!latest) return;
  if (currentBundle === null) {
    currentBundle = latest; // first successful read = the running version
    return;
  }
  if (latest !== currentBundle) updatePending = true;
};

const reloadIfPending = (): void => {
  if (updatePending) reloadOnce();
};

const installNavigationReload = (): void => {
  const originalPush = window.history.pushState.bind(window.history);
  const originalReplace = window.history.replaceState.bind(window.history);

  window.history.pushState = (
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ): void => {
    originalPush(data, unused, url);
    // Reload after the navigation commits, so the new route renders fresh.
    if (updatePending) window.setTimeout(reloadOnce, 0);
  };
  window.history.replaceState = (
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ): void => {
    originalReplace(data, unused, url);
    if (updatePending) window.setTimeout(reloadOnce, 0);
  };

  window.addEventListener('popstate', reloadIfPending);
};

const isChunkError = (message: string): boolean =>
  /Loading chunk [\d]+ failed/i.test(message) ||
  /ChunkLoadError/i.test(message) ||
  /Loading CSS chunk/i.test(message) ||
  /Failed to fetch dynamically imported module/i.test(message);

const installChunkErrorReload = (): void => {
  window.addEventListener('error', (event: ErrorEvent) => {
    const message = event?.message || event?.error?.message || '';
    if (isChunkError(String(message))) reloadOnce();
  });
  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      const reason = event?.reason;
      const message = (reason && (reason.message ?? reason)) || '';
      if (isChunkError(String(message))) reloadOnce();
    },
  );
};

export const initVersionCheck = (): void => {
  if (typeof window === 'undefined') return;

  installChunkErrorReload();
  installNavigationReload();

  // Seed the currently-running version, then poll for newer builds.
  void checkForUpdate();
  window.setInterval(() => void checkForUpdate(), POLL_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    // On refocus, refresh the pending flag so the user's next navigation
    // reloads into the new build. We intentionally do not reload here.
    if (document.visibilityState === 'visible') void checkForUpdate();
  });
};
